import type { Vertex } from '@baduk/engine';

/**
 * What a tap or click on the board means.
 *
 * A mouse and a fingertip want opposite things here. A mouse is already
 * hovering the point before it clicks — the ghost stone has been sitting there
 * showing exactly where the stone will land — so a click should play. A
 * fingertip covers several intersections at once and arrives with no warning:
 * on a 390px phone an intersection is about 36px at 9x9 and under 18px at
 * 19x19, so the first tap is a guess. Playing on it is how a stone ends up
 * somewhere nobody chose.
 *
 * So on touch the first tap only *aims*: it puts the ghost down, lights up the
 * group and liberties under it, and shows the coach's grade for it. The second
 * tap on the same point plays. Aiming and inspecting are deliberately the same
 * gesture — hovering already did both on desktop, and a learner should not have
 * to know a separate one to look at the board.
 *
 * This lives apart from the component because there is no browser here to click
 * through. Logic left inside an event handler cannot be tested at all.
 */

export type PointerKind = 'mouse' | 'touch';

export type TapOutcome =
  /** Show the ghost, the group and liberties, and the grade. Play nothing. */
  | { readonly kind: 'aim'; readonly vertex: Vertex }
  /** Play the stone. */
  | { readonly kind: 'commit'; readonly vertex: Vertex };

export interface TapInput {
  readonly vertex: Vertex;
  /** Where the aim currently sits, if anywhere. */
  readonly aimed: Vertex | null;
  readonly pointer: PointerKind;
  /** A stone is already there. */
  readonly occupied: boolean;
  /** The rules allow a stone here. */
  readonly legal: boolean;
  /** The board is not accepting moves — a bot is thinking, or nobody is playing. */
  readonly disabled: boolean;
}

export function tapOutcome(input: TapInput): TapOutcome {
  const { vertex, aimed, pointer, occupied, legal, disabled } = input;
  const playable = !occupied && legal && !disabled;

  // Anything unplayable aims instead, and that is the useful half of the
  // feature rather than a consolation. Aiming an occupied point is how you read
  // a group's liberties, which is the most important thing the board can teach;
  // aiming an illegal one is how the coach gets to explain a ko ban or a
  // suicide, rather than the tap silently doing nothing.
  if (!playable) return { kind: 'aim', vertex };

  // A mouse has been hovering this point all along — the ghost is already
  // there, so the click is the confirmation.
  if (pointer === 'mouse') return { kind: 'commit', vertex };

  // A fingertip has to say it twice, and tapping anywhere else moves the aim
  // rather than playing there. A misjudged tap costs a correction, not a stone.
  return aimed === vertex ? { kind: 'commit', vertex } : { kind: 'aim', vertex };
}
